import { DateTime } from 'luxon';
import { z } from 'zod';
import { apiEnvelope } from '../envelope';
import { aiThemeConfigSchema, memberPlanKindSchema } from '../plans/schema';
import { ianaTimezoneSchema } from '../timezone';

/**
 * How many Sona conversations the starter plan asks for before the first plan
 * review opens. The intake conversation counts as the first (ADR-116 Rule 3),
 * so a freshly onboarded member opens at one.
 */
export const INITIAL_REVIEW_REQUIRED_CONVERSATIONS = 4;
export const INITIAL_REVIEW_MAX_PLAN_DAYS = 7;
export const INITIAL_REVIEW_MAX_RECAP_DAYS = 9;
export const INITIAL_REVIEW_STATES = ['not_due', 'due', 'in_progress', 'completed'] as const;

export const initialReviewStateSchema = z.enum(INITIAL_REVIEW_STATES);

export const initialReviewCalendarDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'must be an ISO calendar date (YYYY-MM-DD)')
  .refine(
    (value) => DateTime.fromISO(value, { zone: 'utc' }).isValid,
    'must be a real calendar date',
  );

export const initialReviewPeriodSchema = z
  .object({
    startDate: initialReviewCalendarDateSchema,
    endDate: initialReviewCalendarDateSchema,
  })
  .strict()
  .refine(({ startDate, endDate }) => startDate <= endDate, {
    message: 'startDate must not be after endDate',
    path: ['endDate'],
  });

export type InitialReviewState = z.infer<typeof initialReviewStateSchema>;
export type InitialReviewPeriod = z.infer<typeof initialReviewPeriodSchema>;
export const INITIAL_REVIEW_V2_FLOW_VERSION = 'initial-review-v2' as const;
export const INITIAL_REVIEW_V2_STEP_KEY = 'check_ins' as const;
export const INITIAL_REVIEW_PRESENTATION_FAMILIES = ['quantified_check_ins_v1'] as const;
export const initialReviewPresentationFamilySchema = z.enum(INITIAL_REVIEW_PRESENTATION_FAMILIES);
/**
 * The copy groups a program's `initial_review.copy_ref` may name — the keys of
 * the locale catalog's `initial_review.presentation` group, mirrored here so
 * bundles that never load the catalog (the home summary Lambda) can still fail
 * an unknown ref closed. A parity test in apps/api pins this list to the
 * catalog; adding a copy group means adding its key here in the same change.
 */
export const INITIAL_REVIEW_PRESENTATION_COPY_REFS = [
  'quantified_substance_v1',
  'alcohol_v1',
  'yes_no_substance_v1',
] as const;

/**
 * Capability negotiation for the review projection. The query parameter name,
 * the accepted values, and the parse to a tier live here so every consumer —
 * both API route contracts, the home summary handler, and the mobile API
 * modules — reads one definition instead of restating the enum.
 */
export const PLAN_REVIEW_PROJECTION_QUERY = 'planReviewProjection' as const;
/**
 * `v1` adds the plan-kind projection; `v2` the weekly review entry; `v3` says
 * the client parses the starter plan's `planProgress` with its nullable end
 * fields — a bundle below `v3` is served no `planProgress` for a starter plan
 * rather than a shape it would reject wholesale.
 */
export const PLAN_REVIEW_PROJECTIONS = ['v1', 'v2', 'v3'] as const;
export const planReviewProjectionSchema = z.enum(PLAN_REVIEW_PROJECTIONS);
export type PlanReviewProjection = z.infer<typeof planReviewProjectionSchema>;
/** `legacy` is the tier served when the client sends no (or an unknown) projection. */
export type PlanReviewProjectionTier = PlanReviewProjection | 'legacy';
/** The query string current clients append to every review request. */
export const PLAN_REVIEW_PROJECTION_V2_QUERY = `${PLAN_REVIEW_PROJECTION_QUERY}=v2` as const;
/** The query string the home summary client appends — it parses the starter plan's projection. */
export const PLAN_REVIEW_PROJECTION_V3_QUERY = `${PLAN_REVIEW_PROJECTION_QUERY}=v3` as const;

export function parsePlanReviewProjection(
  raw: string | null | undefined,
): PlanReviewProjectionTier {
  const parsed = planReviewProjectionSchema.safeParse(raw);
  return parsed.success ? parsed.data : 'legacy';
}

/** Whether a negotiated tier is at least `floor` — tiers are additive, so a newer client keeps every older shape. */
export function planReviewProjectionAtLeast(
  tier: PlanReviewProjectionTier,
  floor: PlanReviewProjection,
): boolean {
  if (tier === 'legacy') return false;
  return PLAN_REVIEW_PROJECTIONS.indexOf(tier) >= PLAN_REVIEW_PROJECTIONS.indexOf(floor);
}

export const initialReviewRecapDaySchema = z
  .object({
    date: initialReviewCalendarDateSchema,
    weekdayLabel: z.string().min(1),
    dateLabel: z.string().min(1),
    accessibilityLabel: z.string().min(1),
    state: z.enum(['zero_quantity', 'positive_quantity', 'missing']),
    quantity: z.number().int().nonnegative().nullable(),
  })
  .strict()
  .superRefine((day, context) => {
    const validQuantity =
      (day.state === 'zero_quantity' && day.quantity === 0) ||
      (day.state === 'positive_quantity' && day.quantity !== null && day.quantity > 0) ||
      (day.state === 'missing' && day.quantity === null);
    if (!validQuantity) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['quantity'],
        message: 'quantity must match the recap day state',
      });
    }
  });

const initialReviewRecapCoreSchema = z
  .object({
    reviewId: z.string().uuid(),
    flowVersion: z.literal(INITIAL_REVIEW_V2_FLOW_VERSION),
    stepKey: z.literal(INITIAL_REVIEW_V2_STEP_KEY),
    period: z
      .object({
        startDate: initialReviewCalendarDateSchema,
        endDate: initialReviewCalendarDateSchema,
        displayLabel: z.string().min(1),
      })
      .strict()
      .refine(({ startDate, endDate }) => startDate <= endDate, {
        message: 'startDate must not be after endDate',
        path: ['endDate'],
      }),
    activity: z
      .object({
        completedConversationCount: z.number().int().nonnegative(),
        daysTracked: z.number().int().nonnegative(),
      })
      .strict(),
    days: z.array(initialReviewRecapDaySchema).min(1).max(INITIAL_REVIEW_MAX_RECAP_DAYS),
    totalQuantity: z.number().int().nonnegative(),
    freeDayCount: z.number().int().nonnegative(),
    copy: z
      .object({
        screenAccessibilityTitle: z.string().min(1),
        daySummaryAccessibilityLabel: z.string().min(1),
        introTitle: z.string().min(1),
        introBody: z.string().min(1),
        introSectionLabel: z.string().min(1),
        introActivityStepLabel: z.string().min(1),
        introGapsStepLabel: z.string().min(1),
        introPlanStepLabel: z.string().min(1),
        introContinueLabel: z.string().min(1),
        activityTitle: z.string().min(1),
        activityConversationsLabel: z.string().min(1),
        activityDaysTrackedLabel: z.string().min(1),
        activityContinueLabel: z.string().min(1),
        eyebrow: z.string().min(1),
        title: z.string().min(1),
        targetText: z.string().min(1),
        comparisonText: z.string().min(1),
        freeLabel: z.string().min(1),
        trackedLabel: z.string().min(1),
        missingLabel: z.string().min(1),
        totalQuantityLabel: z.string().min(1),
        freeDayCountLabel: z.string().min(1),
        totalQuantityAccessibilityLabel: z.string().min(1),
        freeDayCountAccessibilityLabel: z.string().min(1),
        addMissingLabel: z.string().min(1),
        skipLabel: z.string().min(1),
        continueLabel: z.string().min(1),
        quantityUnitLabel: z.string().min(1),
        missingBadgeLabel: z.string().min(1),
        sheetTitle: z.string().min(1),
        saveLabel: z.string().min(1),
        repairErrorMessage: z.string().min(1),
        decrementLabel: z.string().min(1),
        incrementLabel: z.string().min(1),
      })
      .strict(),
    repair: z
      .object({
        available: z.boolean(),
        quantityLimit: z.number().int().positive(),
        dates: z
          .array(
            z
              .object({
                date: initialReviewCalendarDateSchema,
                weekdayLabel: z.string().min(1),
                dateLabel: z.string().min(1),
                accessibilityLabel: z.string().min(1),
                decrementAccessibilityLabel: z.string().min(1),
                incrementAccessibilityLabel: z.string().min(1),
              })
              .strict(),
          )
          .max(INITIAL_REVIEW_MAX_PLAN_DAYS),
      })
      .strict(),
  })
  .strict();

function validateInitialReviewRecap(
  recap: z.infer<typeof initialReviewRecapCoreSchema>,
  context: z.RefinementCtx,
): void {
  const issue = (path: Array<string | number>, message: string) =>
    context.addIssue({ code: 'custom', path, message });
  const dayDates = recap.days.map(({ date }) => date);
  if (new Set(dayDates).size !== dayDates.length) {
    issue(['days'], 'recap days must be unique');
  }

  const missingDates = new Set(
    recap.days.filter(({ state }) => state === 'missing').map(({ date }) => date),
  );
  const repairDates = recap.repair.dates.map(({ date }) => date);
  const repairDateSet = new Set(repairDates);
  if (repairDateSet.size !== repairDates.length) {
    issue(['repair', 'dates'], 'repair dates must be unique');
  }
  if (recap.repair.available !== missingDates.size > 0) {
    issue(['repair', 'available'], 'repair availability must match missing recap days');
  }
  if (
    repairDateSet.size !== missingDates.size ||
    Array.from(missingDates).some((date) => !repairDateSet.has(date))
  ) {
    issue(['repair', 'dates'], 'repair dates must exactly match missing recap days');
  }

  const totalQuantity = recap.days.reduce((total, day) => total + (day.quantity ?? 0), 0);
  if (recap.totalQuantity !== totalQuantity) {
    issue(['totalQuantity'], 'total quantity must equal the recap day quantities');
  }
  const freeDayCount = recap.days.filter(({ state }) => state === 'zero_quantity').length;
  if (recap.freeDayCount !== freeDayCount) {
    issue(['freeDayCount'], 'free day count must equal zero-quantity recap days');
  }
  if (recap.activity.daysTracked !== recap.days.length - missingDates.size) {
    issue(['activity', 'daysTracked'], 'days tracked must equal non-missing recap days');
  }
}

/** Exact pre-rollover wire shape served when the client does not opt in. */
export const initialReviewLegacyRecapSchema = initialReviewRecapCoreSchema.superRefine(
  validateInitialReviewRecap,
);

/** Projection served to already-installed planReviewProjection=v1 clients. */
export const initialReviewV1RecapSchema = initialReviewRecapCoreSchema
  .extend({ sourcePlanKind: memberPlanKindSchema })
  .strict()
  .superRefine(validateInitialReviewRecap);

/** Capability-negotiated recap used by current clients. */
export const initialReviewRecapSchema = initialReviewRecapCoreSchema
  .extend({
    sourcePlanKind: memberPlanKindSchema,
    presentation: z
      .object({
        family: initialReviewPresentationFamilySchema,
        memberProgramId: z.string().uuid(),
        checkinSchema: z.string().min(1),
        quantityLimit: z.number().int().positive(),
        copyRef: z.string().min(1),
      })
      .strict(),
    targetComparison: z
      .object({
        targetQuantity: z.number().int().nonnegative(),
        targetText: z.string().min(1),
        comparisonText: z.string().min(1),
      })
      .strict()
      .nullable(),
  })
  .strict()
  .superRefine(validateInitialReviewRecap);

const initialReviewFacadeCoreShape = {
  state: initialReviewStateSchema,
  completedConversationCount: z.number().int().nonnegative(),
  requiredConversationCount: z.number().int().positive(),
  period: initialReviewPeriodSchema.nullable(),
  entryVisible: z.boolean(),
};

/** Strict pre-rollover response retained for installed clients. */
export const initialReviewLegacyFacadeDataSchema = z
  .object({ ...initialReviewFacadeCoreShape, recap: initialReviewLegacyRecapSchema.nullable() })
  .strict();

/**
 * The projected facade shapes differ only in their recap tier; the four
 * optional plan-review projection fields are shared. One factory keeps them
 * from drifting the way hand-transcribed copies would.
 */
function facadeDataSchema<T extends z.ZodTypeAny>(recapSchema: T) {
  return z
    .object({
      ...initialReviewFacadeCoreShape,
      recap: recapSchema.nullable(),
      sourcePlanKind: memberPlanKindSchema.optional(),
      reviewDate: initialReviewCalendarDateSchema.nullable().optional(),
      timezone: ianaTimezoneSchema.nullable().optional(),
      reviewId: z.string().uuid().nullable().optional(),
    })
    .strict();
}

export const initialReviewV1FacadeDataSchema = facadeDataSchema(initialReviewV1RecapSchema);

export const initialReviewFacadeDataSchema = facadeDataSchema(initialReviewRecapSchema);

export const completePlanReviewRequestSchema = z
  .object({
    reviewId: z.string().uuid(),
    suggestionSetId: z.string().uuid(),
    selectedSuggestionIds: z
      .array(z.string().min(1))
      .min(1)
      .max(3)
      .refine((ids) => new Set(ids).size === ids.length, {
        message: 'selected suggestion ids must be unique',
      }),
    requestKey: z.string().uuid(),
  })
  .strict();

export const completedPlanThemeLegacySchema = aiThemeConfigSchema
  .extend({
    position: z.number().int().nonnegative(),
    origin: z.literal('ai'),
  })
  .omit({ rank: true })
  .strict();

export const completedPlanThemeSchema = completedPlanThemeLegacySchema
  .extend({ memberProgramId: z.string().uuid().nullable() })
  .strict();

const completePlanReviewCoreShape = {
  reviewId: z.string().uuid(),
  sourcePlanId: z.string().uuid(),
  replayed: z.boolean(),
};

function completedSuccessorPlanSchema<T extends z.ZodTypeAny>(themeSchema: T) {
  return z
    .object({
      id: z.string().uuid(),
      kind: z.literal('weekly'),
      status: z.literal('active'),
      startsOn: initialReviewCalendarDateSchema,
      endsOn: initialReviewCalendarDateSchema,
      timezone: ianaTimezoneSchema,
      themes: z.array(themeSchema).min(1).max(3),
    })
    .strict();
}

export const completePlanReviewLegacyDataSchema = z
  .object({
    ...completePlanReviewCoreShape,
    successorPlan: completedSuccessorPlanSchema(completedPlanThemeLegacySchema),
  })
  .strict();

export const completePlanReviewDataSchema = z
  .object({
    ...completePlanReviewCoreShape,
    successorPlan: completedSuccessorPlanSchema(completedPlanThemeSchema),
  })
  .strict();

export const startInitialReviewV2DataSchema = z
  .object({ resumed: z.boolean(), recap: initialReviewRecapSchema })
  .strict();
export const startInitialReviewLegacyDataSchema = z
  .object({ resumed: z.boolean(), recap: initialReviewLegacyRecapSchema })
  .strict();
export const startInitialReviewV1DataSchema = z
  .object({ resumed: z.boolean(), recap: initialReviewV1RecapSchema })
  .strict();
export const startInitialReviewRequestSchema = z.object({}).strict();
export const repairInitialReviewRequestSchema = z
  .object({
    requestKey: z.string().uuid(),
    days: z
      .array(
        z
          .object({
            date: initialReviewCalendarDateSchema,
            quantity: z.number().int().nonnegative().max(9999),
          })
          .strict(),
      )
      .min(1)
      .max(INITIAL_REVIEW_MAX_PLAN_DAYS),
  })
  .strict();
export const repairInitialReviewDataSchema = z.object({ recap: initialReviewRecapSchema }).strict();
export const repairInitialReviewLegacyDataSchema = z
  .object({ recap: initialReviewLegacyRecapSchema })
  .strict();
export const repairInitialReviewV1DataSchema = z
  .object({ recap: initialReviewV1RecapSchema })
  .strict();

export const initialReviewFacadeResponseSchema = apiEnvelope(initialReviewFacadeDataSchema);
export const initialReviewLegacyFacadeResponseSchema = apiEnvelope(
  initialReviewLegacyFacadeDataSchema,
);
export const initialReviewV1FacadeResponseSchema = apiEnvelope(initialReviewV1FacadeDataSchema);
export const startInitialReviewV2ResponseSchema = apiEnvelope(startInitialReviewV2DataSchema);
export const startInitialReviewV1ResponseSchema = apiEnvelope(startInitialReviewV1DataSchema);
export const startInitialReviewLegacyResponseSchema = apiEnvelope(
  startInitialReviewLegacyDataSchema,
);
export const repairInitialReviewResponseSchema = apiEnvelope(repairInitialReviewDataSchema);
export const repairInitialReviewLegacyResponseSchema = apiEnvelope(
  repairInitialReviewLegacyDataSchema,
);
export const repairInitialReviewV1ResponseSchema = apiEnvelope(repairInitialReviewV1DataSchema);
export const initialReviewCompatibleFacadeResponseSchema = z.union([
  initialReviewFacadeResponseSchema,
  initialReviewV1FacadeResponseSchema,
  initialReviewLegacyFacadeResponseSchema,
]);
export const startInitialReviewCompatibleResponseSchema = z.union([
  startInitialReviewV2ResponseSchema,
  startInitialReviewV1ResponseSchema,
  startInitialReviewLegacyResponseSchema,
]);
export const repairInitialReviewCompatibleResponseSchema = z.union([
  repairInitialReviewResponseSchema,
  repairInitialReviewV1ResponseSchema,
  repairInitialReviewLegacyResponseSchema,
]);
export const completePlanReviewResponseSchema = apiEnvelope(completePlanReviewDataSchema);
export const completePlanReviewLegacyResponseSchema = apiEnvelope(
  completePlanReviewLegacyDataSchema,
);
export const completePlanReviewCompatibleResponseSchema = z.union([
  completePlanReviewResponseSchema,
  completePlanReviewLegacyResponseSchema,
]);

export type InitialReviewRecapDay = z.infer<typeof initialReviewRecapDaySchema>;
export type InitialReviewRecap = z.infer<typeof initialReviewRecapSchema>;
export type InitialReviewFacadeData = z.infer<typeof initialReviewFacadeDataSchema>;
export type StartInitialReviewV2Data = z.infer<typeof startInitialReviewV2DataSchema>;
export type RepairInitialReviewRequest = z.infer<typeof repairInitialReviewRequestSchema>;
export type RepairInitialReviewData = z.infer<typeof repairInitialReviewDataSchema>;
export type CompletePlanReviewRequest = z.infer<typeof completePlanReviewRequestSchema>;
export type CompletePlanReviewData = z.infer<typeof completePlanReviewDataSchema>;
